import { useEffect, useState, useMemo } from 'react';
import {
  checkIsGradient,
  isColorReference,
  resolveColorReference,
  rgbaToHexa,
} from '@builder4/shared/utils/styleHelpers';
import type { Color, Gradient, RGBA } from '@builder4/shared/types/layout';
import { ColorField } from './ColorField';
import { cn } from '@/lib/utils';
import { useAppSelector } from '@/store/hooks';
import { selectActiveGlobalStyle } from '@/store/globalStyles/selectors';
import { createPaletteFromGlobalColors } from '@/utils/colorPaletteHelpers';
import { useInspectorNode } from '@/hooks/useInspectorNode';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import { useInspectorHelper } from '@/hooks/useInspectorHelper';
import type { Palette } from '../ColorPicker/ColorPicker';
import { isTextBlockNode } from '@/utils/helperFunc';
import { editorRegistry } from '@/components/userComponents/Text';
import { applyMark, getCurrentMarkValue } from '@/utils/textUtils';
import type { CustomText } from '@builder4/shared/types/text';

interface NodeColorFieldProps {
  propertyName: string;
  label?: string;
  className?: string;
  gradientEnabled?: boolean;
  solidPropertyName?: string;
  gradientPropertyName?: string;
  canClear?: boolean;
  targetDisplayName?: string;
  palettes?: Palette[];
  ensureStrokeVisibleProp?: string;
  isSlateMarksMode?: boolean; // New prop to explicitly indicate Slate Marks mode
}

const NodeColorField = ({
  propertyName,
  label = 'Fill',
  className = 'max-w-[216px]',
  gradientEnabled = false,
  solidPropertyName,
  gradientPropertyName,
  canClear = true,
  targetDisplayName,
  palettes,
  ensureStrokeVisibleProp,
  isSlateMarksMode,
}: NodeColorFieldProps) => {
  const dataTestId = `color-field-${label.toLowerCase().replace(/\s+/g, '-')}`;
  const activeStyles = useAppSelector(selectActiveGlobalStyle);
  const { id, props, node, actions } = useInspectorNode<Record<string, unknown>>();
  const { ensureStrokeVisible } = useInspectorHelper();

  const displayName = targetDisplayName || node?.data.displayName || 'Unknown';
  const { setBPProp, getBPProp } = useBreakpointAwareProp(displayName);

  const isTextNode = node ? isTextBlockNode(node) : false;

  // Helper: Get global text color for Text components with applied global styles
  const getGlobalTextColor = (): Color | string => {
    if (!props.appliedGlobalStyle || !props.selectedStyleVariant) {
      return undefined;
    }

    const [category, subVariant] = (props.selectedStyleVariant as string).split('.') as [
      'heading' | 'body' | 'link',
      string,
    ];

    if (category === 'heading') {
      const headingTag = subVariant as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
      return activeStyles.typography.heading[headingTag]?.fontColor;
    }

    if (category === 'body') {
      const bodySize = subVariant as 'small' | 'medium' | 'large';
      return activeStyles.typography.body?.[bodySize]?.fontColor;
    }

    if (category === 'link') {
      const linkVariant = subVariant as 'link1' | 'link2';
      return activeStyles.typography.link?.[linkVariant]?.fontColor;
    }

    return undefined;
  };

  // Helper: Get value from Slate marks or props with global style fallback
  const getValueFromSource = <T,>(propName: string): T | undefined => {
    if (isTextNode && isSlateMarksMode) {
      const slateEditor = editorRegistry.get(id);
      if (slateEditor) {
        // Check for Slate editor override (user's per-selection formatting)
        const key = propName as keyof Omit<CustomText, 'text'>;
        const override = getCurrentMarkValue(slateEditor.editor, key) as T | undefined;

        if (override !== undefined) {
          return override;
        }
        // Fall back to global text color if applicable
        const globalColor = getGlobalTextColor();
        if (globalColor !== undefined) {
          if (propName === 'color') {
            return globalColor as T;
          }
          // For solid/gradient properties, extract the appropriate type
          if (propName === 'textSolid' && typeof globalColor !== 'string') {
            return (checkIsGradient(globalColor) ? undefined : globalColor) as T;
          }
          if (propName === 'textGradient' && typeof globalColor !== 'string') {
            return (checkIsGradient(globalColor) ? globalColor : undefined) as T;
          }
        }
      }
    }

    return getBPProp(props, propName) as T;
  };

  // Get color values from appropriate source (Slate marks or props)
  const colorValue = getValueFromSource<Color | string>(propertyName);
  const gradientValue = gradientPropertyName
    ? getValueFromSource<Gradient>(gradientPropertyName)
    : undefined;
  const solidValue = solidPropertyName ? getValueFromSource<RGBA>(solidPropertyName) : undefined;
  const strokeValue = ensureStrokeVisibleProp
    ? (getBPProp(props, ensureStrokeVisibleProp) as number)
    : 0;

  // Get hex value for input
  const getColorHex = (): string => {
    if (isColorReference(colorValue)) {
      const resolved = resolveColorReference(colorValue, activeStyles.colors);
      if (!resolved) return '';
      if (checkIsGradient(resolved)) {
        return '';
      }
      return rgbaToHexa(resolved);
    }
    return rgbaToHexa(solidValue || colorValue);
  };

  // Get gradient value
  const getGradient = (): Gradient | null => {
    if (isColorReference(colorValue)) {
      const resolved = resolveColorReference(colorValue, activeStyles.colors);
      if (!resolved) return null;
      if (checkIsGradient(resolved)) {
        return resolved;
      }
      return null;
    }
    return gradientValue || null;
  };

  // Get color value
  const getColor = (): Color | null => {
    if (isColorReference(colorValue)) {
      return resolveColorReference(colorValue, activeStyles.colors);
    }
    return colorValue;
  };

  // Determine if current value is gradient
  const isGradient = useMemo((): boolean => {
    if (isColorReference(colorValue)) {
      const resolved = resolveColorReference(colorValue, activeStyles.colors);
      return checkIsGradient(resolved || '');
    }
    return checkIsGradient(colorValue || '');
  }, [colorValue, gradientValue, activeStyles.colors]);

  const [colorHex, setColorHex] = useState(getColorHex());
  const [gradient, setGradient] = useState<Gradient | null>(getGradient());

  useEffect(() => {
    setColorHex(getColorHex());
  }, [solidValue, colorValue, activeStyles.colors]);

  useEffect(() => {
    setGradient(getGradient());
  }, [gradientValue, colorValue, activeStyles.colors]);

  const handleOnChangeComplete = (newColor: Color | string, initialColor: Color | string) => {
    const hadNoColor = !colorValue;

    // ========== Slate Marks Mode ==========
    if (isTextNode && isSlateMarksMode) {
      const slateEditor = editorRegistry.get(id);
      if (!slateEditor) return;
      const { editor } = slateEditor;

      // Apply color to Slate editor marks
      if (isColorReference(newColor)) {
        applyMark(editor, propertyName, newColor);
      } else {
        const mode = checkIsGradient(newColor) ? 'gradient' : 'solid';
        const newValue = newColor ? JSON.parse(JSON.stringify(newColor)) : undefined;
        applyMark(editor, propertyName, newValue);

        // Apply gradient/solid specific marks if enabled
        if (gradientEnabled) {
          if (mode === 'gradient') {
            applyMark(editor, 'textGradient', newValue);
          } else {
            applyMark(editor, 'textSolid', newValue);
          }
        }
      }
      return;
    }

    // ========== Standard Props Mode ==========
    actions.history.ignore().setProp(id, (props: Record<string, unknown>) => {
      if (initialColor) {
        if (isColorReference(initialColor)) {
          setBPProp(props, propertyName, initialColor);
        } else {
          const initialColorValue = JSON.parse(JSON.stringify(initialColor));
          setBPProp(props, propertyName, initialColorValue);
          if (gradientEnabled) {
            const mode = checkIsGradient(initialColor) ? 'gradient' : 'solid';
            if (mode === 'gradient' && gradientPropertyName) {
              setBPProp(props, gradientPropertyName, initialColorValue);
            } else if (mode === 'solid' && solidPropertyName) {
              setBPProp(props, solidPropertyName, initialColorValue);
            }
          }
        }
      } else {
        setBPProp(props, propertyName, undefined);
        if (gradientEnabled) {
          const mode = checkIsGradient(newColor) ? 'gradient' : 'solid';
          if (mode === 'gradient' && gradientPropertyName) {
            setBPProp(props, gradientPropertyName, undefined);
          } else if (solidPropertyName) {
            setBPProp(props, solidPropertyName, undefined);
          }
        }
      }
    });

    actions.setProp(id, (props: Record<string, unknown>) => {
      if (isColorReference(newColor)) {
        setBPProp(props, propertyName, newColor);
      } else {
        const newColorValue = newColor ? JSON.parse(JSON.stringify(newColor)) : undefined;
        setBPProp(props, propertyName, newColorValue);
        if (gradientEnabled) {
          const mode = checkIsGradient(newColor) ? 'gradient' : 'solid';
          if (mode === 'gradient' && gradientPropertyName) {
            setBPProp(props, gradientPropertyName, newColorValue);
          } else if (solidPropertyName) {
            setBPProp(props, solidPropertyName, newColorValue);
          }
        }
      }

      if (ensureStrokeVisibleProp && hadNoColor && newColor) {
        ensureStrokeVisible(ensureStrokeVisibleProp, strokeValue);
      }
    });
  };

  const handleOnChange = (value: Color | undefined, mode?: 'solid' | 'gradient') => {
    const hadNoColor = !colorValue;

    // ==========  Slate Marks Mode ==========
    // For Text components, onChange doesn't need Slate updates (only onChangeComplete does)

    if (isTextNode && isSlateMarksMode) {
      return;
    }

    // ========== Standard Props Mode ==========
    actions.history.ignore().setProp(id, (props: Record<string, unknown>) => {
      const newValue = value ? JSON.parse(JSON.stringify(value)) : undefined;
      setBPProp(props, propertyName, newValue);
      if (gradientEnabled) {
        if (mode === 'gradient' && gradientPropertyName) {
          setBPProp(props, gradientPropertyName, newValue);
        } else if (mode === 'solid' && solidPropertyName) {
          setBPProp(props, solidPropertyName, newValue as RGBA);
        }
      }

      if (ensureStrokeVisibleProp && hadNoColor && value) {
        ensureStrokeVisible(ensureStrokeVisibleProp, strokeValue);
      }
    });
  };

  const colorPalettes = palettes || [createPaletteFromGlobalColors(activeStyles.colors)];

  return (
    <ColorField
      palettes={colorPalettes}
      className={cn(className)}
      dataTestId={dataTestId}
      mode={isGradient ? 'gradient' : 'solid'}
      gradient={gradient || undefined}
      inputValue={colorHex}
      placeholderText="-"
      state="default"
      text={label}
      type="text"
      color={getColor() || undefined}
      onChange={handleOnChange}
      onChangeComplete={handleOnChangeComplete}
      canClear={canClear}
      hasGradient={gradientEnabled}
    />
  );
};

export default NodeColorField;
